/**
 * Error handling utilities for consistent error processing across the frontend
 */

import { ApiError } from '@/services/api'

/**
 * Extract error message from various error types (ApiError objects, Error instances, etc.)
 * @param error - The error object to extract message from
 * @param fallbackMessage - Message to use if no valid error message is found
 * @returns The extracted error message
 */
export const getErrorMessage = (error: unknown, fallbackMessage: string = 'An unexpected error occurred'): string => {
  // Handle API errors (ApiError object from api.ts) and standard Error objects
  if (error && typeof error === 'object' && 'message' in error) {
    const errorMessage = (error as any).message as string
    return errorMessage || fallbackMessage
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error || fallbackMessage
  }
  
  // Fallback for unknown error types
  return fallbackMessage
}

/**
 * Check if an error message indicates a specific validation error
 * @param error - The error object
 * @param validationType - The type of validation error to check for (case-insensitive)
 * @returns True if the error matches the validation type
 */
export const isValidationError = (error: unknown, validationType: string): boolean => {
  const message = getErrorMessage(error, '')
  return message.toLowerCase().includes(validationType.toLowerCase())
}

/**
 * Type guard to check if an error is an ApiError
 * @param error - The error to check
 * @returns True if the error is an ApiError
 */
export const isApiError = (error: unknown): error is ApiError => {
  return error !== null && 
         typeof error === 'object' && 
         'message' in error && 
         'status' in error
}

export default {
  getErrorMessage,
  isValidationError,
  isApiError
} 